import { Edit02 } from "@untitledui/icons";
import clsx from "clsx";
import type { ComponentType, SVGProps } from "react";

export type EnrollmentConfirmationCardFooter = {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  text: string;
};

export type EnrollmentConfirmationCardProps = {
  children?: React.ReactNode;
  footer?: EnrollmentConfirmationCardFooter;
  headerSrc?: string;
  onEdit: () => void;
  editTestId?: string;
  tight?: boolean;
  title: string;
};

export type EnrollmentRequirementProps = {
  label: string;
  children: React.ReactNode;
};

export const EnrollmentRequirement = ({
  label,
  children,
}: EnrollmentRequirementProps) => (
  <div className="flex min-w-0 flex-col">
    <div className="text-sm text-secondary">{label}</div>
    <div className="text-sm break-words text-primary">{children}</div>
  </div>
);

type EditButtonProps = {
  onEdit: () => void;
  testId?: string;
  className?: string;
};

const EditButton = ({ onEdit, testId, className }: EditButtonProps) => (
  <button
    type="button"
    aria-label="Edit"
    data-testid={testId}
    onClick={onEdit}
    className={clsx(
      "flex shrink-0 items-center justify-center rounded-full text-tertiary",
      className,
    )}
  >
    <Edit02 className="size-5" />
  </button>
);

const CardFooter = ({
  footer,
}: {
  footer: EnrollmentConfirmationCardFooter;
}) => {
  const Icon = footer.icon;
  return (
    <div className="flex items-center justify-center gap-2 bg-success-25 px-4 py-3">
      <Icon className="size-4 shrink-0 stroke-[2.25px] text-success-700" />
      <p className="flex-1 text-sm text-success-primary">{footer.text}</p>
    </div>
  );
};

export const EnrollmentConfirmationCard = ({
  children,
  footer,
  headerSrc,
  onEdit,
  editTestId,
  tight,
  title,
}: EnrollmentConfirmationCardProps) => (
  <div className="flex w-full flex-col overflow-hidden rounded-xl border border-secondary bg-primary">
    {headerSrc && (
      <div className="relative h-19 w-full">
        <img
          src={headerSrc}
          alt=""
          className="absolute inset-0 size-full object-cover"
        />
        <EditButton
          onEdit={onEdit}
          testId={editTestId}
          className="absolute right-1.5 top-4 bg-white/90 p-2.5"
        />
      </div>
    )}
    <div className={clsx("flex flex-col p-4", tight ? "gap-0" : "gap-4")}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-secondary">{title}</div>
        {!headerSrc && (
          <EditButton
            onEdit={onEdit}
            testId={editTestId}
            className="-my-2 -mr-2 p-2"
          />
        )}
      </div>
      {children && (
        <div className="grid grid-cols-2 gap-x-2 gap-y-4 [&>*:last-child:nth-child(odd)]:col-span-2">
          {children}
        </div>
      )}
    </div>
    {footer && <CardFooter footer={footer} />}
  </div>
);
